import Link from 'next/link';
import { memo, useCallback, useEffect, useState } from 'react';
import { type DropTargetMonitor, useDrop } from 'react-dnd';

import { useTrackSidebarInteraction } from '@air/analytics';
import { type LibraryWithPermissions } from '@air/api/types';
import { TreeItemAction } from '@air/component-tree';
import { Ellipsis, Plus, TriangleDown } from '@air/next-icons';
import { DropdownMenu, type DropdownMenuProps } from '@air/primitive-dropdown-menu';
import { VisuallyHidden } from '@air/primitive-visually-hidden';
import { useAirModal } from '@air/provider-modal';

import { useLibraryRootBoards } from '~/components/LibraryBeta/hooks/queries/useLibraryRootBoards';
import { LibraryNaigationDropdownMenuContent } from '~/components/LibraryBeta/LibraryNavigation/components/LibraryNavigationDropdownMenuContent';
import { CreateBoardModal } from '~/components/Modals/CreateBoardModal';
import { boardDragTypes, DragType, type DropData, isBoardDragType } from '~/components/Shared/Drag/dragTypes';
import { DNDLibraryDrag } from '~/components/v2/components/shared/dnd/DNDLibraryDrag';
import {
  WorkspaceSidebarBoardsList,
  type WorkspaceSidebarBoardsListProps,
} from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarBoardsList';
import { useWorkspaceSidebarSectionStore } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarSectionStoreContext';
import { useWorkspaceSidebarPushLock } from '~/components/v2/hooks/useWorkspaceSidebarPushLock';
import { getCanvasQueryParams } from '~/constants/canvasQueryParams';
import { Pathname } from '~/constants/pathname';
import { useFetchObjectsPermissions } from '~/hooks/useFetchObjectsPermissions';
import { useLibraryBoardRearrange } from '~/hooks/useLibraryBoardRearrange';
import { useLibraryPermissionsCache } from '~/hooks/useLibraryPermissionsCache';
import { useURLLibraryIdSelector } from '~/hooks/useURLLibraryIdSelector';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { isCentralizedBoardInCurrentLibrarySelector } from '~/store/centralizedBoard/selectors';
import { selectedBoardsSelector } from '~/store/selectedItems/selectors';
import { canCreateBoard } from '~/utils/permissions/boardPermissions';
import { useAirSelector, useAirStore } from '~/utils/ReduxUtils';

export type WorkspaceSidebarLibrariesListItemProps = Pick<WorkspaceSidebarBoardsListProps, 'scrollElement'> & {
  library: LibraryWithPermissions;
  draggable?: boolean;
  index?: number;
};

export const WorkspaceSidebarLibrariesListItem = memo<WorkspaceSidebarLibrariesListItemProps>(
  ({ library, draggable = false, index = 0 }) => {
    const open = useWorkspaceSidebarSectionStore((store) => !!store.getTreeItem({ id: library.id })?.open);
    const touched = useWorkspaceSidebarSectionStore((store) => !!store.getTreeItem({ id: library.id })?.touched);
    const setTreeItem = useWorkspaceSidebarSectionStore((state) => state.setTreeItem);
    const toggleTreeItemOpen = useWorkspaceSidebarSectionStore((state) => state.toggleTreeItemOpen);
    const { currentWorkspace } = useCurrentWorkspace();
    const currentLibraryId = useURLLibraryIdSelector();
    const { trackSidebarInteraction } = useTrackSidebarInteraction();

    // Use stored state if touched, otherwise auto-expand when library is active
    const isLibraryActive = currentLibraryId === library.id;

    const onToggleOpen = useCallback(() => {
      toggleTreeItemOpen({ id: library.id });
      trackSidebarInteraction({
        action: open ? 'collapse' : 'expand',
        type: 'library',
      });
    }, [toggleTreeItemOpen, library.id, open, trackSidebarInteraction]);

    const { data: libraryRootBoards, isInitialLoading: isLoadingBoards } = useLibraryRootBoards({
      workspaceId: currentWorkspace?.id,
      libraryId: library.id,
      enabled: open,
    });
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [showCreateBoardModal] = useAirModal(CreateBoardModal);
    const store = useAirStore();
    const selectedBoards = selectedBoardsSelector(store.getState());
    const { getLibraryPermissions } = useLibraryPermissionsCache();

    const isCentralizedBoardInCurrentLibrary = useAirSelector((state) =>
      isCentralizedBoardInCurrentLibrarySelector(state, library.id),
    );

    useFetchObjectsPermissions({
      objects: {
        boardIds: libraryRootBoards.map((board) => board.id),
      },
    });

    useEffect(() => {
      if (touched) {
        return;
      }

      if (isCentralizedBoardInCurrentLibrary) {
        setTreeItem({ id: library.id, open: true, touched: false });
      }
    }, [isCentralizedBoardInCurrentLibrary, library.id, setTreeItem, touched]);

    const checkIfCanDrop = useCallback(
      (monitor: DropTargetMonitor) => {
        const item = monitor.getItem();

        if (!item) {
          return false;
        }

        const libraryPermissions = getLibraryPermissions(library.id);

        if (!canCreateBoard(libraryPermissions)) {
          return false;
        }

        if (isBoardDragType(item.type) || selectedBoards.length) {
          const isDifferentLibrary = item?.firstItem?.library?.id !== library.id;

          return isDifferentLibrary;
        }

        return false;
      },
      [getLibraryPermissions, library.id, selectedBoards.length],
    );

    const [{ isOver, canDrop }, drop] = useDrop({
      accept: boardDragTypes,
      drop: (): DropData => ({
        type: 'move-to-library',
        location: 'library',
        data: library,
      }),
      collect: (monitor) => ({
        isOver: monitor.isOver(),
        canDrop: monitor.canDrop(),
      }),
      canDrop: (_props, monitor) => {
        const itemType = monitor.getItemType();

        // Libraries can't be dropped on other libraries
        if (itemType === DragType.library) {
          return false;
        }

        return checkIfCanDrop(monitor);
      },
    });

    const onLibraryClick = useCallback(() => {
      if (touched || open) {
        trackSidebarInteraction({
          action: 'link_click',
          type: 'library',
        });
      } else {
        setTreeItem({ id: library.id, open: true, touched: false });

        trackSidebarInteraction({
          action: 'link_click_and_expand',
          type: 'library',
        });
      }
    }, [touched, open, library.id, setTreeItem, trackSidebarInteraction]);

    const libraryPermissions = getLibraryPermissions(library.id);
    const canCreateLibraryBoard = canCreateBoard(libraryPermissions);

    const { onBoardRearrange, getCanRearrange } = useLibraryBoardRearrange({
      library,
      libraryPermissions,
    });

    const onCreateBoard = useCallback(() => {
      showCreateBoardModal({
        initialLibrary: library,
        trackLocation: 'library',
      });

      trackSidebarInteraction({
        action: 'create_board',
        type: 'library',
      });
    }, [showCreateBoardModal, library, trackSidebarInteraction]);

    const { handleOpenChange: handleLockOpenChange } = useWorkspaceSidebarPushLock({
      token: `sidebar-library-${library.id}`,
    });

    const onDropdownOpenChange = useCallback<Required<DropdownMenuProps>['onOpenChange']>(
      (open) => {
        setIsMenuOpen(open);
        handleLockOpenChange(open);

        if (open) {
          trackSidebarInteraction({
            action: 'open_sub_menu',
            type: 'library',
          });
        }
      },
      [handleLockOpenChange, trackSidebarInteraction],
    );

    return (
      <div className="flex flex-col gap-1">
        <DNDLibraryDrag
          className="data-[dnd-dragging=true]:opacity-50"
          library={library}
          enabled={draggable}
          index={index}
        >
          <div
            ref={drop}
            className="group relative flex h-10 shrink-0 cursor-pointer items-center gap-3 rounded-8 px-2 font-normal text-14 text-grey-11 outline-none transition-colors hover:bg-grey-5 focus-visible:ring-2 focus-visible:ring-blue-9 focus-visible:ring-inset active:bg-grey-5 data-[dnd-over=true]:data-[dnd-droppable=true]:z-10 data-[dnd-over=true]:data-[dnd-droppable=true]:bg-grey-3 data-[dnd-over=true]:data-[dnd-droppable=true]:ring-2 data-[dnd-over=true]:data-[dnd-droppable=true]:ring-blue-9 data-[active=true]:bg-grey-5 data-[menu-open=true]:bg-grey-5"
            data-active={isLibraryActive}
            data-dnd-over={isOver}
            data-dnd-droppable={canDrop}
            data-menu-open={isMenuOpen}
            data-open={open}
          >
            <div
              className="relative flex size-6 shrink-0 cursor-pointer items-center justify-center rounded-4 bg-grey-4 text-14 ring-1 ring-grey-4 transition-colors group-hover:ring-transparent"
              style={{ backgroundColor: library.color?.backgroundHex }}
            >
              <span>{library.icon ?? '📚'}</span>

              <button
                className="absolute inset-0 z-40 flex size-full items-center justify-center rounded-4 bg-grey-5 opacity-0 transition-[background-color,opacity] hover:bg-grey-4 focus-visible:opacity-100 group-hover:opacity-100"
                onClick={onToggleOpen}
                type="button"
                draggable={false}
              >
                <VisuallyHidden>{open ? 'Collapse' : 'Expand'} library</VisuallyHidden>
                <TriangleDown className="size-4 text-grey-12 transition-transform group-data-[open=false]:-rotate-90 group-data-[open=true]:rotate-0" />
              </button>
            </div>

            <Link
              className="absolute inset-0 z-30 size-full"
              href={{
                pathname: Pathname.LibraryByLibraryId,
                query: {
                  ...getCanvasQueryParams(),
                  libraryId: library.id,
                },
              }}
              onClick={onLibraryClick}
              draggable={false}
            >
              <VisuallyHidden>Go to {library.title}</VisuallyHidden>
            </Link>

            {/* Title - handles navigation only */}
            <span className="grow truncate">{library.title}</span>

            {/* Library Actions */}
            <div className="relative z-40 hidden shrink-0 items-center gap-1 transition-opacity group-focus-within:flex group-hover:flex group-data-[menu-open=true]:flex">
              <DropdownMenu
                align="start"
                onOpenChange={onDropdownOpenChange}
                open={isMenuOpen}
                trigger={
                  <TreeItemAction data-testid="LIBRARY_MENU_BUTTON">
                    <Ellipsis className="size-4" />
                  </TreeItemAction>
                }
              >
                <LibraryNaigationDropdownMenuContent library={library} />
              </DropdownMenu>

              {canCreateLibraryBoard && (
                <TreeItemAction data-testid="ADD_LIBRARY_BOARD_BUTTON" onClick={onCreateBoard}>
                  <Plus className="size-4" />
                </TreeItemAction>
              )}
            </div>
          </div>
        </DNDLibraryDrag>

        {!isLoadingBoards && libraryRootBoards?.length > 0 && open && (
          <WorkspaceSidebarBoardsList
            boards={libraryRootBoards}
            depth={2}
            dragType={DragType.boardNavLibrary}
            draggable={true}
            droppable={true}
            getCanRearrange={getCanRearrange}
            onBoardRearrange={onBoardRearrange}
            parentBoardId={null}
          />
        )}
      </div>
    );
  },
);

WorkspaceSidebarLibrariesListItem.displayName = 'WorkspaceSidebarLibrariesListItem';
